import React, { useRef } from 'react';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { Link, useLocation } from 'react-router-dom';
import SvgOpenExternal from '../svg/OpenExternal';
import SvgXIcon from '../svg/XIcon';
import DropdownMenu from '../shared/dropdownMenu/DropdownMenu';
import DropdownMenuItem from '../shared/dropdownMenu/DropdownMenuItem';
import {
  associateAlarmDeviceText,
  associateAlertDeviceText,
} from '../shared/SharedInternationalizations';
import AccessControl from '../../AccessControl';
import permissions from '../../permissionsList';

/**
 * Column header component for use in the units column view
 * @param {*} props
 */

export function UnconnectedUnitsColumnHeader(props) {
  const node = useRef();
  const location = useLocation();
  const { unit, unitArray, usersArray } = props;
  const { pathname, search } = location;
  const currentLocation = pathname + search;
  const unitPlaceholder = {
    name: 'N/A',
    address1: 'N/A',
    address2: 'N/A',
    city: 'N/A',
    state: 'N/A',
    zipcode: 'N/A',
    id: 0,
    is_active: 'N/A',
  };
  const {
    name,
    address1,
    address2,
    city,
    state,
    zipcode,
    id,
    is_active,
    assigned_device_count,
  } = unit || unitPlaceholder;
  const unitchildren = unitArray.filter((unit) => unit.parent_unit === id);
  const unitChildrenIds = unitchildren.map((unit) => unit.id);
  const unitChildrenCount = unitchildren.length;

  let userList = [];
  userList = usersArray.filter((user) => user.units && user.units.includes(id));
  const userArray = userList.map((user) => user.id);

  const deviceCount = assigned_device_count;

  /**
   * Function to calculate the number of users assigned to the unit to be displayed
   * @returns {number} - The total number of users that are assigned to the displayed unit
   * @description - Takes in the array of users from Redux then filters them down to find the number of users that have unit id that matches the id of the currently displayed unit
   */
  const assignedUsersCount = () => {
    let users = [];
    let length = 0;
    users = usersArray.filter((user) => {
    return user && user.units && user.units.includes(id);
  });
  if (users.length > 0) {
    length = users.length;
  }
  return length;
  };

  const viewUnit = (
    <FormattedMessage
      id="unitsColumnDisplay.viewUnitButtonText"
      defaultMessage="View Unit"
      description="A link to open the full view for this unit"
    />
  );
  const editUnit = (
    <FormattedMessage
      id="unitsColumnDisplay.editUnitButtonText"
      defaultMessage="Edit Unit"
      description="A link to open the edit interface for this unit"
    />
  );
  const addChildUnit = (
    <FormattedMessage
      id="unitsColumnDisplay.addChildUnitButtonText"
      defaultMessage="Add A New Child Unit"
      description="A link to open the interface to add a child unit"
    />
  );

  const associateUsersUnits = (
    <FormattedMessage
      id="unitsColumnDisplay.associateUsersToUnitButtonText"
      defaultMessage="Associate Users to Unit"
      description="A link to open the interface to add a child unit"
    />
  );

  const associateChildUnits = (
    <FormattedMessage
      id="unitsColumnDisplay.associateChildUnitsButtonText"
      defaultMessage="Edit Child Units"
      description="A link to open the interface to add a child unit"
    />
  );

  const noName = (
    <FormattedMessage
      id="unitsColumnDisplay.unitNameHeaderNoName"
      defaultMessage="(No Name)"
      description="No unit name was supplied"
    />
  );

  // to ensure that there is correct spacing, these create placeholders for the address information when there isn't text to fill those spaces
  const address1Display = address1 || (
    <div className="column-header__address-placeholder" />
  );

  const address2Display = address2 || (
    <div className="column-header__address-placeholder" />
  );

  let cityStateZipDisplay = (
    <div className="column-header__address-placeholder" />
  );
  // If there are any of the city/state/zip they'll create the correct spacing so this displays whatever of those are available
  if (city || state || zipcode) {
    let cityDisplay = '';
    let stateDisplay = '';
    let zipDisplay = '';
    if (city && state) {
      cityDisplay = `${city},`;
    } else if (city) {
      cityDisplay = `${city}`;
    }
    if (state) {
      stateDisplay = state;
    }
    if (zipcode) {
      zipDisplay = zipcode;
    }
    cityStateZipDisplay = `${cityDisplay} ${stateDisplay} ${zipDisplay}`;
  }

  return (
    <div
      className="units-column-view__column-header"
      ref={node}
      data-test="units-column-header">
      <div className="column-header__upper-container">
        <span
          className={
            name
              ? 'column-header__unit-title text-center text-extra-bold text-extra-large'
              : 'column-header__unit-title text-center text-extra-bold text-extra-large text-dimmed'
          }>
          {name || noName}
        </span>
        <div className="absolute-top-right margin-right-5">
          <DropdownMenu data-test="quick-actions-menu">
            <DropdownMenuItem>
              <Link
                data-test="full-view-quick-actions-link"
                className="dropdown-menu__menu-content-item"
                to={`/units/${id}/view/`}
                state={{
                  unit: id,
                  previous: currentLocation,
                }}>
                {viewUnit}
              </Link>
            </DropdownMenuItem>
            <AccessControl
              data-test="access-control"
              feature={permissions.static.units.editUnit}>
              <DropdownMenuItem
                additionalStyles={
                  is_active ? '' : 'dropdown-menu__menu-item--inactive'
                }>
                <Link
                  data-test="edit-link"
                  className={
                    is_active
                      ? 'dropdown-menu__menu-content-item'
                      : 'dropdown-menu__menu-content-item--inactive'
                  }
                  to={`/units/${id}/edit/`}
                  state={{
                    modal: true,
                    parent_unit: id,
                    previous: currentLocation,
                  }}>
                  {editUnit}
                </Link>
              </DropdownMenuItem>
            </AccessControl>

            <AccessControl
              data-test="access-control"
              feature={permissions.static.units.editUnit}>
              <DropdownMenuItem
                additionalStyles={
                  is_active ? '' : 'dropdown-menu__menu-item--inactive'
                }>
                <Link
                  data-test="associate-users-to-unit-link"
                  className={
                    is_active
                      ? 'dropdown-menu__menu-content-item'
                      : 'dropdown-menu__menu-content-item--inactive'
                  }
                  to={`/units/${id}/edit/?mode=associateUsers`}
                  state={{
                    modal: true,
                    users: userArray,
                    previous: currentLocation,
                  }}>
                  {associateUsersUnits}
                </Link>
              </DropdownMenuItem>
            </AccessControl>

            <AccessControl
              data-test="access-control"
              feature={permissions.static.units.addUnit}>
              <DropdownMenuItem
                additionalStyles={
                  is_active ? '' : 'dropdown-menu__menu-item--inactive'
                }>
                <Link
                  data-test="add-child-unit-link"
                  className={
                    is_active
                      ? 'dropdown-menu__menu-content-item'
                      : 'dropdown-menu__menu-content-item--inactive'
                  }
                  to={`addUnit`}
                  state={{
                    modal: true,
                    parent_unit: id,
                    previous: currentLocation,
                  }}>
                  {addChildUnit}
                </Link>
              </DropdownMenuItem>
            </AccessControl>

            <AccessControl
              data-test="access-control"
              feature={permissions.static.units.editUnit}>
              <DropdownMenuItem
                additionalStyles={
                  is_active ? '' : 'dropdown-menu__menu-item--inactive'
                }>
                <Link
                  data-test="edit-child-units-link"
                  className={
                    is_active
                      ? 'dropdown-menu__menu-content-item'
                      : 'dropdown-menu__menu-content-item--inactive'
                  }
                  to={`/units/${id}/edit/?mode=associateChildUnits`}
                  state={{
                    modal: true,
                    initialUnits: unitChildrenIds,
                    previous: currentLocation,
                  }}>
                  {associateChildUnits}
                </Link>
              </DropdownMenuItem>
            </AccessControl>

            <AccessControl
              data-test="access-control"
              feature={permissions.static.devices.editDeviceAssociation}>
              <DropdownMenuItem
                additionalStyles={
                  is_active ? '' : 'dropdown-menu__menu-item--inactive'
                }>
                <Link
                  data-test="associate-alarm-device-link"
                  className={
                    is_active
                      ? 'dropdown-menu__menu-content-item'
                      : 'dropdown-menu__menu-content-item--inactive'
                  }
                  to={`/units/${id}/edit/?mode=associateAlarmDevice`}
                  state={{
                    modal: true,
                    previous: currentLocation,
                  }}>
                  {associateAlarmDeviceText}
                </Link>
              </DropdownMenuItem>
            </AccessControl>

            <AccessControl
              data-test="access-control"
              feature={permissions.static.devices.editDeviceAssociation}>
              <DropdownMenuItem
                additionalStyles={
                  is_active ? '' : 'dropdown-menu__menu-item--inactive'
                }>
                <Link
                  data-test="associate-alert-device-link"
                  className={
                    is_active
                      ? 'dropdown-menu__menu-content-item'
                      : 'dropdown-menu__menu-content-item--inactive'
                  }
                  to={`/units/${id}/edit/?mode=associateAlertDevice`}
                  state={{
                    modal: true,
                    previous: currentLocation,
                  }}>
                  {associateAlertDeviceText}
                </Link>
              </DropdownMenuItem>
            </AccessControl>

            <AccessControl
              data-test="access-control"
              feature={permissions.static.devices.addDeviceAssociation}>
              <DropdownMenuItem
                data-test="swap-fixed-device-link"
                additionalStyles={
                  is_active ? '' : 'dropdown-menu__menu-item--inactive'
                }>
                <a
                  data-test="swap-device-link"
                  className={
                    is_active
                      ? 'dropdown-menu__menu-content-item'
                      : 'dropdown-menu__menu-content-item--inactive'
                  }
                  href="/utils/device_swap.php">
                  <FormattedMessage
                    id="unitColumnHeader.swapFixedDeviceText"
                    defaultMessage="Swap a Fixed Device"
                    description="The user phone number"
                  />
                </a>
              </DropdownMenuItem>
            </AccessControl>

            <AccessControl
              data-test="access-control"
              feature={permissions.static.devices.addDeviceAssociation}>
              <DropdownMenuItem
                data-test="swap-pendant-link"
                additionalStyles={
                  is_active ? '' : 'dropdown-menu__menu-item--inactive'
                }>
                <a
                  data-test="swap-pendant-link"
                  className={
                    is_active
                      ? 'dropdown-menu__menu-content-item'
                      : 'dropdown-menu__menu-content-item--inactive'
                  }
                  href="/utils/pendant_swap.php">
                  <FormattedMessage
                    id="unitColumnHeader.swapPendantText"
                    defaultMessage="Swap a Pendant"
                    description="The user phone number"
                  />
                </a>
              </DropdownMenuItem>
            </AccessControl>
          </DropdownMenu>
        </div>
        <span
          className="column-header__address"
          data-test="column-header-address1">
          {address1Display}
        </span>
        <span
          className="column-header__address"
          data-test="column-header-address2">
          {address2Display}
        </span>
        <span
          className="column-header__address"
          data-test="column-header-city-state-zip">
          {cityStateZipDisplay}
        </span>
      </div>
      <div
        className="column-header__lower-container"
        data-test="column-header-metrics-container">
        <div className="column-header__metrics-container">
          <span
            className="text-primary text-large text-extra-bold"
            data-test="assigned-users-count">
            {assignedUsersCount()}
          </span>
          <FormattedMessage
            id="unitsColumnDisplay.unitNameHeaderAssignedUsers"
            defaultMessage="Assigned Users"
            description="The number of users assigned to this unit">
            {(txt) => <span className="text-small">{txt}</span>}
          </FormattedMessage>
        </div>
        <div className="column-header__metrics-container">
          <span
            className="text-primary text-large text-extra-bold"
            data-test="assigned-devices-count">
            {deviceCount}
          </span>
          <FormattedMessage
            id="unitsColumnDisplay.unitNameHeaderDevices"
            defaultMessage="Devices"
            description="The number of devices assigned to this unit">
            {(txt) => <span className="text-small">{txt}</span>}
          </FormattedMessage>
        </div>
        <div className="column-header__metrics-container">
          <span
            className="text-primary text-large text-extra-bold"
            data-test="child-units-count">
            {unitChildrenCount}
          </span>
          <FormattedMessage
            id="unitsColumnDisplay.unitNameHeaderChildUnits"
            defaultMessage="Child Units"
            description="The number of child units assigned to this unit">
            {(txt) => <span className="text-small">{txt}</span>}
          </FormattedMessage>
        </div>

        <FormattedMessage
          id="unitsColumnDisplay.unitNameHeaderFullViewLink"
          defaultMessage="Full View"
          data-test="units-column-header-full-view-link-text"
          description="A link to view more details about a unit">
          {(txt) => (
            <Link
              className="column-header__details-link text-small"
              data-test="units-column-header-full-view-link"
              to={`/units/${id}/view/`}
              state={{
                unit: id,
                previous: currentLocation,
              }}>
              {txt} <SvgOpenExternal className="margin-left-10 text-small" />
            </Link>
          )}
        </FormattedMessage>
      </div>
      <AccessControl
        data-test="access-control"
        feature={permissions.static.units.addUnit}>
        {is_active ? (
          <Link
            data-test="inner-column-add-unit-button"
            className={
              'link link-deep-blue no-margin column-header__add-child-button text-medium'
            }
            to={`addUnit`}
            state={{
              modal: true,
              parent_unit: id,
              previous: currentLocation,
            }}>
            <span />
            {addChildUnit}
            <SvgXIcon className="rotate-45-cw" />
          </Link>
        ) : (
          ''
        )}
      </AccessControl>
    </div>
  );
}
const mapStateToProps = (state, ownProps) => {
  const unit = state.units.units[ownProps.unitId];
  const unitArray = Object.values(state.units.units);
  const usersArray = Object.values(state.users.users);
  const alertDevices = Object.values(state.devices.alertDeviceDetails);
  const alarmDevices = Object.values(state.devices.alarmDeviceDetails);

  return {
    unitObj: state.units.units,
    unitArray,
    usersArray,
    unit,
    alertDevices,
    alarmDevices,
  };
};
export default connect(mapStateToProps)(UnconnectedUnitsColumnHeader);
